import { supabase } from '../client';
import type { Database } from '../generated';

/** Codegen may lag `sales_tax_rate_pct` until the next `db:types` run. */
export type StoreSettings = Database['core']['Tables']['store_settings']['Row'] & {
  sales_tax_rate_pct?: number | string | null;
};

export interface StoreSettingsInput {
  displayName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactAddress?: string | null;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  replyToEmail?: string | null;
  orderPrefix?: string | null;
  invoicePrefix?: string | null;
  /** Sales tax rate percent (0–100). Null/undefined leaves unchanged on upsert. */
  salesTaxRatePct?: number | null;
}

const CLEARABLE_FIELDS = [
  'display_name',
  'contact_email',
  'contact_phone',
  'contact_address',
  'logo_url',
  'website_url',
  'reply_to_email',
] as const;

export type ClearableStoreField = (typeof CLEARABLE_FIELDS)[number];

/**
 * Read the per-store brand + numbering settings via the
 * `core.rpc_get_store_settings` security-definer RPC. The RPC seeds a
 * row from `core.stores.name` if one is missing, so the caller always
 * gets back a row.
 *
 * Throws if the caller is not a member of the store's org (the RPC
 * raises `42501`). Surface that as a permission error in the UI.
 */
export async function getStoreSettings(storeId: string): Promise<StoreSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_get_store_settings', { p_store_id: storeId });
  if (error) throw error;
  return data as unknown as StoreSettings;
}

/**
 * Upsert (admin/owner only). Pass `null` or an empty string to leave
 * a field unchanged. To explicitly clear a brand field use
 * `clearStoreSettingsField` -- the upsert RPC merges by design.
 *
 * Numbering prefixes default to 'PRO'/'INV' on first insert and are
 * uppercased + validated by the DB (CHECK regex
 * `^[A-Z0-9][A-Z0-9-]{0,15}$`).
 */
export async function upsertStoreSettings(
  storeId: string,
  patch: StoreSettingsInput,
): Promise<StoreSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_upsert_store_settings', {
      p_store_id: storeId,
      p_display_name: patch.displayName ?? null,
      p_contact_email: patch.contactEmail ?? null,
      p_contact_phone: patch.contactPhone ?? null,
      p_contact_address: patch.contactAddress ?? null,
      p_logo_url: patch.logoUrl ?? null,
      p_website_url: patch.websiteUrl ?? null,
      p_reply_to_email: patch.replyToEmail ?? null,
      p_order_prefix: patch.orderPrefix ?? null,
      p_invoice_prefix: patch.invoicePrefix ?? null,
      p_sales_tax_rate_pct: patch.salesTaxRatePct ?? null,
    });
  if (error) throw error;
  return data as unknown as StoreSettings;
}

/**
 * Explicitly clear a brand field. Prefix fields cannot be cleared --
 * they always have a value (default 'PRO'/'INV').
 */
export async function clearStoreSettingsField(
  storeId: string,
  field: ClearableStoreField,
): Promise<StoreSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_clear_store_settings_field', {
      p_store_id: storeId,
      p_field: field,
    });
  if (error) throw error;
  return data as unknown as StoreSettings;
}
